import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { ONTARIO_CITIES } from "../lib/ontarioCities";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Trash, CheckCircle, Image as ImageIcon, PencilSimple, WarningCircle, UploadSimple, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import TollsPanel from "../components/TollsPanel";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TYPES = [
  { v: "parking", l: "Parking" },
  { v: "speeding", l: "Speeding" },
  { v: "toll", l: "Toll / 407" },
  { v: "red_light", l: "Red Light" },
  { v: "other", l: "Other" },
];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const photoUrl = (u) => (u ? `${BACKEND}${u}` : "");
const blank = {
  ticket_number: "", violation_datetime: "", ticket_type: "parking", location: "",
  amount: "", due_date: "", notes: "", photo_url: "", photo_file_id: "",
  booking_id: "", customer_id: "", customer_name: "", vehicle_id: "", vehicle_label: "", vehicle_plate: "",
};

export default function Tickets() {
  return (
    <div className="space-y-6" data-testid="charges-page">
      <div>
        <div className="label-mono">Rentals</div>
        <h2 className="font-heading text-3xl mt-1">Tickets &amp; 407 Tolls</h2>
        <p className="text-sm text-muted-foreground mt-1">Track client tickets and 407 highway tolls — recovered from each client's deposit after their rental.</p>
      </div>
      <Tabs defaultValue="tickets">
        <TabsList data-testid="charges-tabs">
          <TabsTrigger value="tickets" data-testid="tab-tickets">Tickets</TabsTrigger>
          <TabsTrigger value="tolls" data-testid="tab-tolls">407 Tolls</TabsTrigger>
        </TabsList>
        <TabsContent value="tickets" className="mt-6">
          <TicketsPanel />
        </TabsContent>
        <TabsContent value="tolls" className="mt-6">
          <TollsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TicketsPanel() {
  const [tickets, setTickets] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank);
  const [match, setMatch] = useState(null); // {matched, booking}
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showUnmatched, setShowUnmatched] = useState(true);
  const [search, setSearch] = useState("");
  const fileRef = useRef(null);
  const [customCities, setCustomCities] = useState([]);
  const [addingCity, setAddingCity] = useState(false);
  const [newCity, setNewCity] = useState("");

  const load = useCallback(async () => {
    try {
      const [t, r, c] = await Promise.all([
        api.get("/tickets"),
        api.get("/bookings", { params: { booking_type: "rental" } }),
        api.get("/ticket-cities"),
      ]);
      setTickets(t.data);
      setRentals(r.data);
      setCustomCities(c.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cityOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of [...ONTARIO_CITIES, ...customCities, form.location].filter(Boolean)) {
      const key = c.toLowerCase();
      if (!seen.has(key)) { seen.add(key); out.push(c); }
    }
    return out.sort((a, b) => a.localeCompare(b));
  }, [customCities, form.location]);

  const saveNewCity = async () => {
    const name = newCity.trim();
    if (!name) return;
    try {
      const res = await api.post("/ticket-cities", { name });
      setCustomCities(res.data || []);
      set("location", name);
      setNewCity(""); setAddingCity(false);
      toast.success(`Added "${name}"`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-match when plate + datetime are present (skip if user manually overrode).
  useEffect(() => {
    if (editingId || form.booking_id) return;
    if (!form.vehicle_plate) { setMatch(null); return; }
    const id = setTimeout(async () => {
      try {
        const res = await api.get("/tickets/match", {
          params: { plate: form.vehicle_plate, violation_datetime: form.violation_datetime || "" },
        });
        setMatch(res.data);
      } catch { setMatch(null); }
    }, 450);
    return () => clearTimeout(id);
  }, [form.vehicle_plate, form.violation_datetime, form.booking_id, editingId]);

  const openNew = () => { setForm(blank); setEditingId(null); setMatch(null); setAddingCity(false); setNewCity(""); setOpen(true); };
  const openEdit = (t) => {
    setForm({ ...blank, ...t, amount: t.amount ?? "" });
    setEditingId(t.id); setMatch(null); setAddingCity(false); setNewCity(""); setOpen(true);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/tickets/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_url", res.data.url);
      set("photo_file_id", res.data.file_id);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/tickets/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = res.data;
      toast.success(`Imported ${d.imported} ticket(s) · ${d.skipped_duplicates} duplicate(s) skipped · ${d.unmatched} unmatched`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (editingId) await api.put(`/tickets/${editingId}`, payload);
      else await api.post("/tickets", payload);
      toast.success(editingId ? "Ticket updated" : "Ticket added");
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const payOne = async (t) => {
    try { await api.post(`/tickets/${t.id}/pay`); toast.success("Marked paid"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const payAll = async (customer_id, name) => {
    try {
      const res = await api.post("/tickets/pay-all", { customer_id });
      toast.success(`Paid ${res.data.paid} ticket(s) for ${name}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete ticket ${t.ticket_number || ""}?`)) return;
    try { await api.delete(`/tickets/${t.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Group tickets by client; unmatched (no client at all) go to a pinned section.
  const isUnmatched = (t) => !t.customer_id && !(t.customer_name && t.customer_name.trim());
  const q = search.trim().toLowerCase();
  const { unmatchedItems, groupList } = useMemo(() => {
    const matchTicket = (t) =>
      !q ||
      [t.ticket_number, t.customer_name, t.vehicle_label, t.vehicle_plate, t.location, t.ticket_type, t.notes, String(t.amount ?? "")]
        .some((x) => (x || "").toString().toLowerCase().includes(q));
    const filtered = tickets.filter(matchTicket);
    const unmatched = filtered.filter(isUnmatched);
    const g = {};
    for (const t of filtered) {
      if (isUnmatched(t)) continue;
      const key = t.customer_id || `name:${t.customer_name}`;
      if (!g[key]) g[key] = { customer_id: t.customer_id, name: t.customer_name || "Unassigned", items: [], recent: "" };
      g[key].items.push(t);
      const c = t.created_at || "";
      if (c > g[key].recent) g[key].recent = c;
    }
    return { unmatchedItems: unmatched, groupList: Object.values(g).sort((a, b) => (b.recent || "").localeCompare(a.recent || "")) };
  }, [tickets, q]);
  const totalOutstanding = tickets.filter((t) => t.status !== "paid").reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <p className="text-sm text-muted-foreground">
          Outstanding tickets across all clients: <span className="text-primary font-heading">{money(totalOutstanding)}</span>
        </p>
        <div className="flex gap-2 items-center flex-wrap">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ticket #, client, plate, city…" className="bg-input border-border h-9 w-64" data-testid="ticket-search" />
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onImport} className="hidden" data-testid="ticket-import-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-tickets-btn">
            <UploadSimple size={14} className="mr-1.5" /> {importing ? "Importing…" : "Import Ticket CSV"}
          </Button>
          <Button onClick={openNew} data-testid="add-ticket-btn"><Plus size={14} className="mr-1.5" /> Add ticket</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading tickets…</div>
      ) : groupList.length === 0 && unmatchedItems.length === 0 ? (
        <div className="border border-border bg-card p-12 text-center text-muted-foreground" data-testid="tickets-empty">
          {q ? "No tickets match your search." : "No tickets yet. Click “Add ticket” to log one."}
        </div>
      ) : (
        <>
        {unmatchedItems.length > 0 && (
          <div className="border border-amber-700/50 bg-amber-950/10" data-testid="ticket-unmatched-group">
            <button type="button" onClick={() => setShowUnmatched((v) => !v)} className="w-full px-6 py-4 border-b border-amber-700/40 flex items-center justify-between gap-3 text-left" data-testid="ticket-unmatched-toggle">
              <div className="flex items-center gap-2">
                {showUnmatched ? <CaretDown size={16} className="text-amber-500" /> : <CaretRight size={16} className="text-amber-500" />}
                <div>
                  <h3 className="font-heading text-lg flex items-center gap-2"><WarningCircle size={18} className="text-amber-500" /> Unmatched</h3>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {unmatchedItems.length} ticket(s) with no matching rental or client · total {money(unmatchedItems.reduce((s, t) => s + (t.amount || 0), 0))} — edit each to assign a client
                  </div>
                </div>
              </div>
            </button>
            {showUnmatched && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-3 label-mono font-normal">Ticket #</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Type</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Date</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Plate</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Amount</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Status</th>
                    <th className="text-right px-4 py-3 label-mono font-normal">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {unmatchedItems.map((t) => (
                    <tr key={t.id} className="border-b border-border/40" data-testid={`ticket-row-${t.id}`}>
                      <td className="px-4 py-3">{t.ticket_number || "—"}</td>
                      <td className="px-4 py-3 capitalize">{(TYPES.find((x) => x.v === t.ticket_type) || {}).l || t.ticket_type}</td>
                      <td className="px-4 py-3 text-xs">{t.violation_datetime ? new Date(t.violation_datetime).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                      <td className="px-4 py-3 text-xs">{t.vehicle_plate || "—"}</td>
                      <td className="px-4 py-3 font-heading">{money(t.amount)}</td>
                      <td className="px-4 py-3">{t.status === "paid" ? <Badge className="bg-emerald-600 hover:bg-emerald-600">Paid</Badge> : <Badge variant="destructive">Unpaid</Badge>}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" title="Assign client / edit" onClick={() => openEdit(t)} data-testid={`edit-ticket-${t.id}`}><PencilSimple size={15} /></Button>
                          <Button size="icon" variant="ghost" title="Delete" onClick={() => remove(t)} data-testid={`delete-ticket-${t.id}`}><Trash size={15} className="text-destructive" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {groupList.map((g) => {
          const groupTotal = g.items.reduce((s, t) => s + (t.amount || 0), 0);
          const unpaid = g.items.filter((t) => t.status !== "paid");
          const unpaidTotal = unpaid.reduce((s, t) => s + (t.amount || 0), 0);
          return (
            <div key={g.customer_id || g.name} className="border border-border bg-card" data-testid={`ticket-group-${g.customer_id || g.name}`}>
              <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-heading text-lg">{g.name}</h3>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {g.items.length} ticket(s) · total {money(groupTotal)} · <span className="text-destructive">{money(unpaidTotal)} unpaid</span>
                  </div>
                </div>
                {g.customer_id && unpaid.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => payAll(g.customer_id, g.name)} data-testid={`pay-all-${g.customer_id}`}>
                    <CheckCircle size={14} className="mr-1.5" /> Pay all ({unpaid.length})
                  </Button>
                )}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-3 label-mono font-normal">Ticket #</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Type</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Date</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Vehicle</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Amount</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Status</th>
                    <th className="text-right px-4 py-3 label-mono font-normal">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((t) => (
                    <tr key={t.id} className="border-b border-border/40" data-testid={`ticket-row-${t.id}`}>
                      <td className="px-4 py-3">{t.ticket_number || "—"}</td>
                      <td className="px-4 py-3 capitalize">{(TYPES.find((x) => x.v === t.ticket_type) || {}).l || t.ticket_type}</td>
                      <td className="px-4 py-3 text-xs">{t.violation_datetime ? new Date(t.violation_datetime).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                      <td className="px-4 py-3 text-xs">{t.vehicle_label || t.vehicle_plate || "—"}</td>
                      <td className="px-4 py-3 font-heading">{money(t.amount)}</td>
                      <td className="px-4 py-3">
                        {t.status === "paid"
                          ? <Badge className="bg-emerald-600 hover:bg-emerald-600">Paid</Badge>
                          : <Badge variant="destructive">Unpaid</Badge>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {t.photo_url && (
                            <a href={photoUrl(t.photo_url)} target="_blank" rel="noreferrer" title="View photo" data-testid={`ticket-photo-${t.id}`}>
                              <Button size="icon" variant="ghost"><ImageIcon size={15} /></Button>
                            </a>
                          )}
                          {t.status !== "paid" && (
                            <Button size="icon" variant="ghost" title="Mark paid" onClick={() => payOne(t)} data-testid={`pay-ticket-${t.id}`}>
                              <CheckCircle size={15} className="text-emerald-500" />
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" title="Edit" onClick={() => openEdit(t)} data-testid={`edit-ticket-${t.id}`}>
                            <PencilSimple size={15} />
                          </Button>
                          <Button size="icon" variant="ghost" title="Delete" onClick={() => remove(t)} data-testid={`delete-ticket-${t.id}`}>
                            <Trash size={15} className="text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ticket-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Edit ticket" : "Add ticket"}</DialogTitle>
            <DialogDescription>Auto-matches the rental & client by plate and date; override below if needed.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-mono">Ticket #</Label>
              <Input value={form.ticket_number} onChange={(e) => set("ticket_number", e.target.value)} className="bg-input border-border mt-1.5" data-testid="ticket-number-input" />
            </div>
            <div>
              <Label className="label-mono">Type</Label>
              <Select value={form.ticket_type} onValueChange={(v) => set("ticket_type", v)}>
                <SelectTrigger className="bg-input border-border mt-1.5" data-testid="ticket-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-mono">Violation date / time</Label>
              <Input type="datetime-local" step={300} value={form.violation_datetime} onChange={(e) => set("violation_datetime", e.target.value)} className="bg-input border-border mt-1.5" data-testid="ticket-datetime-input" />
            </div>
            <div>
              <Label className="label-mono">License plate</Label>
              <Input value={form.vehicle_plate} onChange={(e) => set("vehicle_plate", e.target.value)} placeholder="e.g. CABC123" className="bg-input border-border mt-1.5" data-testid="ticket-plate-input" />
            </div>

            {/* Auto-match preview */}
            {!editingId && (
              <div className="col-span-2">
                {match?.matched ? (
                  <div className="border border-emerald-700/50 bg-emerald-950/30 p-3 text-sm flex items-start gap-2" data-testid="ticket-match-found">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    <div>
                      <div className="text-emerald-400">Matched rental</div>
                      <div className="text-muted-foreground text-xs mt-0.5">
                        {match.booking.customer_name || "Unknown client"} · {match.booking.vehicle_label || match.booking.vehicle_plate}
                      </div>
                    </div>
                  </div>
                ) : form.vehicle_plate ? (
                  <div className="border border-amber-700/40 bg-amber-950/20 p-3 text-sm flex items-start gap-2" data-testid="ticket-match-none">
                    <WarningCircle size={16} className="text-amber-500 mt-0.5" />
                    <div className="text-muted-foreground text-xs">No rental auto-matched. Pick the rental manually below if you know it.</div>
                  </div>
                ) : null}
              </div>
            )}

            <div className="col-span-2">
              <Label className="label-mono">Rental (override match)</Label>
              <Select value={form.booking_id || "none"} onValueChange={(v) => set("booking_id", v === "none" ? "" : v)}>
                <SelectTrigger className="bg-input border-border mt-1.5" data-testid="ticket-booking-select"><SelectValue placeholder="Auto-match by plate & date" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Auto-match by plate & date</SelectItem>
                  {rentals.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.customer_name || "Guest"} · {b.vehicle_label || b.vehicle_plate || "—"} · {b.start_datetime ? new Date(b.start_datetime).toLocaleDateString() : "no date"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="label-mono">Amount</Label>
              <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="bg-input border-border mt-1.5" data-testid="ticket-amount-input" />
            </div>
            <div>
              <Label className="label-mono">Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="bg-input border-border mt-1.5" data-testid="ticket-due-input" />
            </div>
            <div className="col-span-2">
              <Label className="label-mono">City</Label>
              {addingCity ? (
                <div className="flex gap-2 mt-1.5">
                  <Input autoFocus value={newCity} onChange={(e) => setNewCity(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveNewCity(); } }} placeholder="Type a new city…" className="bg-input border-border" data-testid="ticket-new-city-input" />
                  <Button type="button" onClick={saveNewCity} data-testid="ticket-new-city-save">Add</Button>
                  <Button type="button" variant="ghost" onClick={() => { setAddingCity(false); setNewCity(""); }}>Cancel</Button>
                </div>
              ) : (
                <Select value={form.location || ""} onValueChange={(v) => { if (v === "__add__") { setAddingCity(true); } else { set("location", v); } }}>
                  <SelectTrigger className="bg-input border-border mt-1.5" data-testid="ticket-city-select"><SelectValue placeholder="Select a city" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {cityOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="__add__" className="text-primary" data-testid="ticket-city-add-option">＋ Add city…</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="col-span-2">
              <Label className="label-mono">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-input border-border mt-1.5" data-testid="ticket-notes-input" />
            </div>
            <div className="col-span-2">
              <Label className="label-mono">Ticket photo</Label>
              <div className="flex items-center gap-3 mt-1.5">
                <Input type="file" accept="image/*" onChange={onUpload} className="bg-input border-border" data-testid="ticket-photo-input" />
                {uploading && <span className="text-xs text-muted-foreground">Uploading…</span>}
                {form.photo_url && !uploading && (
                  <a href={photoUrl(form.photo_url)} target="_blank" rel="noreferrer">
                    <img src={photoUrl(form.photo_url)} alt="ticket" className="h-12 w-12 object-cover border border-border" />
                  </a>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="ticket-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="ticket-save-btn">{saving ? "Saving…" : editingId ? "Update" : "Add ticket"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
